import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Box,
  Tooltip,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Avatar,
  Badge,
  Stack,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  MoreVert,
  Person,
  Edit,
  Delete,
  Block,
  CheckCircle,
  Email,
  Login,
  AccessTime,
  AdminPanelSettings,
  ManageAccounts,
  Schedule as ScheduleIcon,
  Engineering,
  Visibility,
  Security,
  VpnKey,
  PersonAdd,
  Settings,
  SupervisorAccount,
} from '@mui/icons-material';

import { UserRole } from "../../types";
import type { User } from "../../types";

interface UserCardProps {
  user: User;
  onEdit?: (user: User) => void;
  onDelete?: (user: User) => void;
  onToggleStatus?: (user: User) => void;
  onResetPassword?: (user: User) => void;
  onViewActivity?: (user: User) => void;
  onChangeRole?: (user: User) => void;
  currentUserRole?: UserRole;
  currentUserId?: number;
}

const UserCard: React.FC<UserCardProps> = ({
  user,
  onEdit,
  onDelete,
  onToggleStatus,
  onResetPassword,
  onViewActivity,
  onChangeRole,
  currentUserRole = UserRole.VIEWER,
  currentUserId,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'error';
      case UserRole.MANAGER:
        return 'warning';
      case UserRole.SCHEDULER:
        return 'info';
      case UserRole.ENGINEER:
        return 'success';
      case UserRole.DIRECTOR:
        return 'secondary';
      case UserRole.VIEWER:
        return 'default';
      default:
        return 'default';
    }
  };

  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return <AdminPanelSettings fontSize="small" />;
      case UserRole.MANAGER:
        return <ManageAccounts fontSize="small" />;
      case UserRole.SCHEDULER:
        return <ScheduleIcon fontSize="small" />;
      case UserRole.ENGINEER:
        return <Engineering fontSize="small" />;
      case UserRole.DIRECTOR:
        return <SupervisorAccount fontSize="small" />;
      case UserRole.VIEWER:
        return <Visibility fontSize="small" />;
      default:
        return <Person fontSize="small" />;
    }
  };

  const formatLastLogin = (lastLogin: string | null): string => {
    if (!lastLogin) return t('users.neverLoggedIn');
    
    const date = new Date(lastLogin);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    if (diffMinutes < 1) return t('users.justLoggedIn');
    if (diffMinutes < 60) return t('users.minutesAgo', { count: diffMinutes });
    if (diffHours < 24) return t('users.hoursAgo', { count: diffHours });
    if (diffDays === 1) return t('common.yesterday');
    if (diffDays < 7) return t('users.daysAgo', { count: diffDays });
    
    return date.toLocaleDateString();
  };

  const formatCreatedDate = (createdAt: string): string => {
    const date = new Date(createdAt);
    return date.toLocaleDateString();
  };

  const canEdit = () => {
    if (currentUserId === user.id) return true; // Редактирование собственного профиля
    return [UserRole.ADMIN].includes(currentUserRole);
  };

  const canDelete = () => {
    if (currentUserId === user.id) return false; // Нельзя удалить самого себя
    return [UserRole.ADMIN].includes(currentUserRole) && user.role !== UserRole.ADMIN;
  };

  const canChangeStatus = () => {
    if (currentUserId === user.id) return false; // Нельзя деактивировать самого себя
    return [UserRole.ADMIN].includes(currentUserRole);
  };

  const canChangeRole = () => {
    if (currentUserId === user.id) return false; // Нельзя изменить свою роль
    return [UserRole.ADMIN].includes(currentUserRole);
  };

  const canResetPassword = () => {
    return [UserRole.ADMIN, UserRole.MANAGER].includes(currentUserRole);
  };

  const canViewActivity = () => {
    return [UserRole.ADMIN, UserRole.MANAGER, UserRole.DIRECTOR].includes(currentUserRole);
  };

  const isCurrentUser = currentUserId === user.id;
  const isOnline = user.last_login && new Date(user.last_login).getTime() > Date.now() - 15 * 60 * 1000; // Онлайн если заходил в последние 15 минут

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        opacity: user.is_active ? 1 : 0.7,
        border: isCurrentUser ? 2 : 1,
        borderColor: isCurrentUser ? 'primary.main' : 'divider',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
      }}
    >
      {/* Заголовок карточки */}
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          {/* Аватар с индикатором онлайн */}
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              isOnline ? (
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: 'success.main',
                    border: 2,
                    borderColor: 'background.paper',
                  }}
                />
              ) : null
            }
          >
            <Avatar
              sx={{
                bgcolor: getRoleColor(user.role),
                width: 56,
                height: 56,
                fontSize: '1.5rem',
              }}
            >
              {user.full_name
                ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase()
                : user.username.charAt(0).toUpperCase()}
            </Avatar>
          </Badge>

          {/* Основная информация */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexGrow: 1,
                }}
              >
                {user.full_name || user.username}
                {isCurrentUser && (
                  <Chip
                    label={t('users.you')}
                    size="small"
                    color="primary"
                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                icon={getRoleIcon(user.role)}
                label={t(`users.userRoles.${user.role.toLowerCase()}`)}
                color={getRoleColor(user.role)}
                size="small"
                variant="outlined"
              />
              <Chip
                icon={user.is_active ? <CheckCircle /> : <Block />}
                label={user.is_active ? t('users.active') : t('users.inactive')}
                color={user.is_active ? 'success' : 'error'}
                size="small"
                variant="filled"
              />
            </Box>

            {user.full_name && user.full_name !== user.username && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                @{user.username}
              </Typography>
            )}
          </Box>

          {/* Меню действий */}
          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreVert />
          </IconButton>
        </Box>

        {/* Контактная информация */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Email fontSize="small" color="action" />
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.email}
            </Typography>
          </Box>
        </Box>

        {/* Информация о активности */}
        <Box sx={{ mb: 2 }}>
          <Stack spacing={0.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Login fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {t('users.lastLogin')}: {formatLastLogin(user.last_login)}
                {isOnline && (
                  <Chip
                    label={t('users.online')}
                    size="small"
                    color="success"
                    sx={{ ml: 1, height: 16, fontSize: '0.65rem' }}
                  />
                )}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonAdd fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {t('users.memberSince')}: {formatCreatedDate(user.created_at)}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Заметки администратора */}
        {user.notes && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              {t('users.notes')}:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                p: 1,
                bgcolor: 'grey.100',
                borderRadius: 1,
                fontSize: '0.875rem',
                fontStyle: 'italic',
              }}
            >
              {user.notes}
            </Typography>
          </Box>
        )}

        {/* Быстрое переключение статуса */}
        {canChangeStatus() && (
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={user.is_active}
                  onChange={() => onToggleStatus?.(user)}
                  size="small"
                />
              }
              label={
                <Typography variant="caption">
                  {user.is_active ? t('users.deactivate') : t('users.activate')}
                </Typography>
              }
            />
          </Box>
        )}
      </CardContent>

      {/* Действия */}
      <CardActions sx={{ mt: 'auto', px: 2, pb: 2 }}>
        <Button
          size="small"
          onClick={() => onViewActivity?.(user)}
          startIcon={<AccessTime />}
          disabled={!canViewActivity()}
        >
          {t('users.activity')}
        </Button>

        {canEdit() && (
          <Button
            size="small"
            onClick={() => onEdit?.(user)}
            startIcon={<Edit />}
            color="primary"
          >
            {isCurrentUser ? t('users.editProfile') : t('common.edit')}
          </Button>
        )}
      </CardActions>

      {/* Контекстное меню */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {canEdit() && onEdit && (
          <MenuItem onClick={() => handleAction(() => onEdit(user))}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {isCurrentUser ? t('users.editProfile') : t('common.edit')}
            </ListItemText>
          </MenuItem>
        )}

        {canChangeRole() && onChangeRole && (
          <MenuItem onClick={() => handleAction(() => onChangeRole(user))}>
            <ListItemIcon>
              <Security fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('users.changeRole')}</ListItemText>
          </MenuItem>
        )}

        {canResetPassword() && onResetPassword && (
          <MenuItem onClick={() => handleAction(() => onResetPassword(user))}>
            <ListItemIcon>
              <VpnKey fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('users.resetPassword')}</ListItemText>
          </MenuItem>
        )}

        {canViewActivity() && onViewActivity && (
          <MenuItem onClick={() => handleAction(() => onViewActivity(user))}>
            <ListItemIcon>
              <AccessTime fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('users.viewActivity')}</ListItemText>
          </MenuItem>
        )}

        {canChangeStatus() && onToggleStatus && (
          <MenuItem onClick={() => handleAction(() => onToggleStatus(user))}>
            <ListItemIcon>
              {user.is_active ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
            </ListItemIcon>
            <ListItemText>
              {user.is_active ? t('users.deactivate') : t('users.activate')}
            </ListItemText>
          </MenuItem>
        )}

        {canDelete() && onDelete && (
          <>
            <Divider />
            <MenuItem 
              onClick={() => handleAction(() => onDelete(user))}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <Delete fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText>{t('common.delete')}</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Card>
  );
};

export default UserCard;
